import classnames from "classnames";
import ReactSelect from "react-select";
import Loading from "../components/Elements/Loading/Loading.tsx";
import { useGet } from "../components/Fetch/useFetch.tsx";
import FilterInput from "../components/Filter/FilterInput.tsx";
import MultiSelectFilterFromList from "../components/Filter/MultiSelectFilterFromList.tsx";
import SelectFilterFromUrl from "../components/Filter/SelectFilterFromUrl.tsx";
import SelectNomenclaturesFilter from "../components/Filter/SelectNomenclaturesFilter.tsx";
import SelectEnumOption from "../components/Form/SelectEnumOption.tsx";
import SelectIdLibelleDataFromList from "../components/Form/SelectIdLibelleFromList.tsx";
import {
  IconAireAspiration,
  IconCloseIndisponibiliteTemporaire,
  IconDocument,
  IconList,
  IconLocation,
  IconOeil,
  IconSee,
  IconVisite,
} from "../components/Icon/Icon.tsx";
import { GET_TYPE_GEOMETRY } from "../components/Localisation/useLocalisation.tsx";
import { ActionColumn, BooleanColumn } from "../components/Table/columns.tsx";
import { usePaginationState } from "../components/Table/Pagination.tsx";
import { columnType } from "../components/Table/QueryTable.tsx";
import {
  ButtonType,
  TYPE_BUTTON,
} from "../components/Table/TableActionColumn.tsx";
import TooltipCustom from "../components/Tooltip/Tooltip.tsx";
import { hasDroit, isAuthorized } from "../droits.tsx";
import UtilisateurEntity from "../Entities/UtilisateurEntity.tsx";
import COLUMN_INDISPONIBILITE_TEMPORAIRE from "../enums/ColumnIndisponibiliteTemporaireEnum.tsx";
import COLUMN_PEI from "../enums/ColumnPeiEnum.tsx";
import DISPONIBILITE_PEI, {
  getLibelleDisponibilite,
} from "../enums/DisponibiliteEnum.tsx";
import TYPE_DROIT from "../enums/DroitEnum.tsx";
import NOMENCLATURES, {
  NOMENCLATURE_ORGANISME,
} from "../enums/NomenclaturesEnum.tsx";
import PROCHAINE_DATE_ENUM from "../enums/ProchaineDateEnum.tsx";
import STATUT_INDISPONIBILITE_TEMPORAIRE from "../enums/StatutIndisponibiliteTemporaireEnum.tsx";
import TYPE_PEI from "../enums/TypePeiEnum.tsx";
import VRAI_FAUX from "../enums/VraiFauxEnum.tsx";
import url from "../module/fetch.tsx";
import DocumentPei from "../pages/Pei/DocumentPei.tsx";
import FicheResume from "../pages/Pei/FicheResume/FicheResume.tsx";
import { URLS } from "../routes.tsx";
import getStringListeAnomalie from "./anomaliesUtils.tsx";
import { AUCUNE_TOURNEE } from "./constantsUtils.tsx";
import formatDateTime, { formatDateWithFallback } from "./formatDateUtils.tsx";
import { IdCodeLibelleType } from "./typeUtils.tsx";

/**
 * Composant filtre pour la disponibilité des PEI
 * Gère la logique spécifique de "Indisponible temporairement" qui met à jour 2 champs
 */
const DisponibiliteTerrrestreFilter = ({
  onChange,
  value,
  libelleNonConforme,
  ...otherProps
}: any) => {
  const defaultValue: IdCodeLibelleType = {
    id: undefined,
    code: undefined,
    libelle: "Tous",
  };

  const listIdCodeLibelle = [
    {
      id: DISPONIBILITE_PEI.DISPONIBLE,
      code: DISPONIBILITE_PEI.DISPONIBLE,
      libelle: "Disponible",
    },
    {
      id: DISPONIBILITE_PEI.INDISPONIBLE,
      code: DISPONIBILITE_PEI.INDISPONIBLE,
      libelle: "Indisponible",
    },
    {
      id: "INDISPONIBLE_TEMPORAIRE",
      code: "INDISPONIBLE_TEMPORAIRE",
      libelle: "Indisponible temporairement",
    },
    {
      id: DISPONIBILITE_PEI.NON_CONFORME,
      code: DISPONIBILITE_PEI.NON_CONFORME,
      libelle: libelleNonConforme,
    },
  ];

  const data = [defaultValue].concat(
    (listIdCodeLibelle || []).map((item: IdCodeLibelleType) => ({
      id: item.id,
      code: item.code ?? item.id,
      libelle: item.libelle,
    })),
  );

  const selectedOption =
    data.find((option) => option.id === value) ?? defaultValue;

  return (
    <ReactSelect
      {...otherProps}
      placeholder={"Sélectionnez"}
      noOptionsMessage={() => "Aucune donnée trouvée"}
      name={"peiDisponibiliteTerrestre"}
      options={data}
      value={selectedOption}
      getOptionValue={(t: IdCodeLibelleType) => t.id ?? ""}
      getOptionLabel={(t: IdCodeLibelleType) => t.libelle}
      onChange={(selected: IdCodeLibelleType) => {
        if (selected?.id === "INDISPONIBLE_TEMPORAIRE") {
          onChange({
            name: "peiDisponibiliteTerrestre",
            value: "INDISPONIBLE_TEMPORAIRE",
          });
          onChange({
            name: "hasIndispoTemp",
            value: true,
          });
        } else {
          onChange({
            name: "peiDisponibiliteTerrestre",
            value: selected?.id,
          });
          onChange({
            name: "hasIndispoTemp",
            value: false,
          });
        }
      }}
    />
  );
};

/**
 * Composant filtre pour les tournées des PEI
 */
const TourneeFilter = ({ onChange, value, ...otherProps }: any) => {
  const defaultValue: IdCodeLibelleType = {
    id: undefined,
    code: undefined,
    libelle: "Tous",
  };

  const nativeOptions = [defaultValue].concat({
    id: AUCUNE_TOURNEE,
    code: AUCUNE_TOURNEE,
    libelle: "Aucune tournée",
  });

  const { isResolved: isResolvedListData, data: listData } = useGet(
    url`/api/tournee/get-libelle-tournee`,
  );

  const options = nativeOptions.concat(
    (listData || []).map((item: { id: string; libelle: string }) => ({
      id: item.id,
      code: item.id,
      libelle: item.libelle,
    })),
  );

  const selectedOption =
    options.find((option) => option.id === value) ?? defaultValue;

  if (!isResolvedListData) {
    return <Loading />;
  } else {
    return (
      <ReactSelect
        {...otherProps}
        placeholder={"Sélectionnez"}
        noOptionsMessage={() => "Aucune donnée trouvée"}
        name={"tourneeId"}
        options={options}
        value={selectedOption}
        getOptionValue={(t: IdCodeLibelleType) => t.id ?? ""}
        getOptionLabel={(t: IdCodeLibelleType) => t.libelle}
        onChange={(selected: IdCodeLibelleType) => {
          if (selected?.id === AUCUNE_TOURNEE) {
            onChange({
              name: "tourneeId",
              value: AUCUNE_TOURNEE,
            });
            onChange({
              name: "hasNoTournee",
              value: true,
            });
          } else {
            onChange({
              name: "tourneeId",
              value: selected?.id,
            });
            onChange({
              name: "hasNoTournee",
              value: false,
            });
          }
        }}
      />
    );
  }
};

type PeiRowType = {
  original: {
    peiId: string;
    peiNumeroComplet: string;
    peiTypePei?: string;
    hasTourneeReservee?: boolean;
    hasIndispoTemp?: boolean;
    hasDocuments?: boolean;
  };
};

function getColumnPeiByStringArray(
  user: UtilisateurEntity,
  parametres: Array<COLUMN_PEI>,
  listeAnomaliePossible: Array<IdCodeLibelleType>,
  fetchGeometry: (typeGeometrie: string, idPei: string) => void,
  displayFicheResumeStandalone: boolean = false,
  delaisWarnCTP: number,
  delaisUrgentCTP: number,
  delaisWarnRECO: number,
  delaisUrgentRECO: number,
  libelleNonConforme: string,
): Array<columnType> {
  const column: Array<columnType> = [];
  const [pagination] = usePaginationState();
  parametres.forEach((_parametre: COLUMN_PEI) => {
    switch (_parametre) {
      case COLUMN_PEI.NUMERO_COMPLET:
        column.push({
          Header: "Numéro",
          accessor: "peiNumeroComplet",
          sortField: "peiNumeroComplet",
          Filter: <FilterInput type="text" name="peiNumeroComplet" />,
          width: 125,
        });
        break;
      case COLUMN_PEI.NUMERO_INTERNE:
        column.push({
          Header: "N° interne",
          accessor: "peiNumeroInterne",
          sortField: "peiNumeroInterne",
          Filter: <FilterInput type="number" name="peiNumeroInterne" />,
          width: 125,
        });
        break;
      case COLUMN_PEI.TYPE_PEI:
        column.push({
          Header: "Type",
          accessor: "peiTypePei",
          sortField: "peiTypePei",
          Filter: <SelectEnumOption options={TYPE_PEI} name={"typePei"} />,
        });
        break;
      case COLUMN_PEI.DISPONIBILITE_TERRESTRE:
        column.push({
          Header: "Disponibilité",
          accessor: (row: {
            peiDisponibiliteTerrestre: keyof typeof DISPONIBILITE_PEI;
            hasIndispoTemp: boolean;
          }) => ({
            peiDisponibiliteTerrestre: row.peiDisponibiliteTerrestre,
            hasIndispoTemp: row.hasIndispoTemp,
          }),
          sortField: "peiDisponibiliteTerrestre",
          Cell: (value) => {
            const dispo =
              DISPONIBILITE_PEI[
                value.value
                  .peiDisponibiliteTerrestre as keyof typeof DISPONIBILITE_PEI
              ] === DISPONIBILITE_PEI.NON_CONFORME
                ? { bg: "bg-warning", value: libelleNonConforme }
                : DISPONIBILITE_PEI[
                      value.value
                        .peiDisponibiliteTerrestre as keyof typeof DISPONIBILITE_PEI
                    ] === DISPONIBILITE_PEI.DISPONIBLE
                  ? { bg: "", value: "Disponible" }
                  : value.value.hasIndispoTemp
                    ? {
                        bg: "bg-danger bg-hachure",
                        value: "Indisponible temporairement",
                      }
                    : { bg: "bg-danger", value: "Indisponible" };
            return (
              <div className={classnames(dispo.bg, "p-1", "rounded")}>
                {dispo.value}
              </div>
            );
          },
          Filter: (
            <DisponibiliteTerrrestreFilter
              libelleNonConforme={libelleNonConforme}
              name={"peiDisponibiliteTerrestre"}
            />
          ),
        });
        break;
      case COLUMN_PEI.DISPONIBILITE_HBE:
        column.push({
          Header: "Disponibilité HBE",
          accessor: "penaDisponibiliteHbe",
          sortField: "penaDisponibiliteHbe",
          Cell: (value) => {
            const dispo =
              DISPONIBILITE_PEI[value.value] === DISPONIBILITE_PEI.NON_CONFORME
                ? { bg: "bg-warning", value: libelleNonConforme }
                : DISPONIBILITE_PEI[value.value] ===
                    DISPONIBILITE_PEI.INDISPONIBLE
                  ? { bg: "bg-danger", value: "Indispo" }
                  : { bg: "", value: "Dispo" };
            return <div className={dispo.bg}>{dispo.value}</div>;
          },
          Filter: (
            <SelectIdLibelleDataFromList
              listIdCodeLibelle={getLibelleDisponibilite(libelleNonConforme)}
              name={"penaDisponibiliteHbe"}
            />
          ),
        });
        break;
      case COLUMN_PEI.NATURE:
        column.push({
          Header: "Nature",
          accessor: "natureLibelle",
          sortField: "natureLibelle",
          Filter: (
            <SelectNomenclaturesFilter
              name={"natureId"}
              nomenclature={NOMENCLATURES.NATURE}
            />
          ),
        });
        break;
      case COLUMN_PEI.COMMUNE:
        column.push({
          Header: "Commune",
          accessor: "communeLibelle",
          sortField: "communeLibelle",
          Filter: (
            <SelectFilterFromUrl
              name={"communeId"}
              url={url`/api/commune/get-libelle-commune`}
            />
          ),
          Cell: (value) => {
            return (
              <TooltipCustom
                tooltipText={value.value}
                tooltipId={value}
                maxWidth={167}
              >
                {value.value}
              </TooltipCustom>
            );
          },
          width: 170,
        });
        break;
      case COLUMN_PEI.NATURE_DECI:
        column.push({
          Header: "Nature DECI",
          accessor: "natureDeciLibelle",
          sortField: "natureDeciLibelle",
          Filter: (
            <SelectNomenclaturesFilter
              name={"natureDeci"}
              nomenclature={NOMENCLATURES.NATURE_DECI}
            />
          ),
        });
        break;
      case COLUMN_PEI.AUTORITE_DECI:
        column.push({
          Header: "Autorité DECI",
          accessor: "autoriteDeci",
          sortField: "autoriteDeci",
          Filter: (
            <SelectFilterFromUrl
              name={"autoriteDeci"}
              url={
                url`/api/organisme/get-list-` +
                NOMENCLATURE_ORGANISME.AUTORITE_DECI
              }
            />
          ),
          width: 150,
        });
        break;
      case COLUMN_PEI.SERVICE_PUBLIC_DECI:
        column.push({
          Header: "Service Public DECI",
          accessor: "servicePublicDeci",
          sortField: "servicePublicDeci",
          Filter: (
            <SelectFilterFromUrl
              name={"servicePublicDeci"}
              url={
                url`/api/organisme/get-list-` +
                NOMENCLATURE_ORGANISME.SERVICE_PUBLIC_DECI
              }
            />
          ),
        });
        break;
      case COLUMN_PEI.ANOMALIES:
        column.push({
          Header: "Anomalies",
          accessor: "listeAnomalie",
          Cell: (value) => {
            return (
              <div>
                {getStringListeAnomalie(value.value, listeAnomaliePossible)}
              </div>
            );
          },
          Filter: (
            <SelectFilterFromUrl
              url={url`/api/anomalie/get-libelle-anomalie`}
              name="anomalieId"
            />
          ),
        });
        break;
      case COLUMN_PEI.PEI_NEXT_ROP:
        column.push({
          Header: "Prochaine ROP",
          accessor: "peiNextRop",
          sortField: "peiNextRop",
          Cell: (value) => {
            return (
              <DateHighlightCell
                date={value.value}
                paramUrgent={delaisUrgentRECO}
                paramWarning={delaisWarnRECO}
              />
            );
          },
          Filter: (
            <SelectEnumOption
              options={PROCHAINE_DATE_ENUM}
              name={"prochaineDateRop"}
            />
          ),
        });
        break;
      case COLUMN_PEI.PEI_NEXT_CTP:
        column.push({
          Header: "Prochain CTP",
          accessor: "peiNextCtp",
          sortField: "peiNextCtp",
          Cell: (value) => {
            return (
              <DateHighlightCell
                date={value.value}
                paramUrgent={delaisUrgentCTP}
                paramWarning={delaisWarnCTP}
              />
            );
          },
          Filter: (
            <SelectEnumOption
              options={PROCHAINE_DATE_ENUM}
              name={"prochaineDateCtp"}
            />
          ),
        });
        break;
      case COLUMN_PEI.TOURNEE_LIBELLE:
        column.push({
          Header: "Tournée",
          accessor: "tourneeLibelle",
          Filter: <TourneeFilter name={"tourneeId"} />,
          width: 250,
          Cell: (value) => {
            return (
              <TooltipCustom
                tooltipText={value.value}
                tooltipId={value}
                maxWidth={247}
              >
                {value.value}
              </TooltipCustom>
            );
          },
        });
        break;

      case COLUMN_PEI.PEI_ADRESSE:
        column.push({
          Header: "Adresse",
          accessor: "adresse",
          sortField: "adresse",
          Filter: <FilterInput type="text" name="adresse" />,
          Cell: (value) => {
            return (
              <TooltipCustom
                tooltipText={value.value}
                tooltipId={value}
                maxWidth={247}
              >
                {value.value}
              </TooltipCustom>
            );
          },
          width: 250,
        });
        break;
      case COLUMN_PEI.GESTIONNAIRE:
        column.push({
          Header: "Gestionnaire",
          accessor: "gestionnaireLibelle",
          sortField: "gestionnaireLibelle",
          Filter: (
            <SelectFilterFromUrl
              url={url`/api/gestionnaire/get-libelle`}
              name="gestionnaireId"
            />
          ),
          width: 250,
          Cell: (value) => {
            return (
              <TooltipCustom
                tooltipText={value.value}
                tooltipId={value}
                maxWidth={247}
              >
                {value.value}
              </TooltipCustom>
            );
          },
        });
        break;

      default:
        column.push({
          Header: "Erreur",
          accessor: "null",
          sortField: "null",
        });
    }
  });

  {
    const listeButton: ButtonType[] = [];

    const canEditPei = isAuthorized(user, [
      TYPE_DROIT.PEI_U,
      TYPE_DROIT.PEI_CARACTERISTIQUES_U,
      TYPE_DROIT.PEI_NUMERO_INTERNE_U,
      TYPE_DROIT.PEI_DEPLACEMENT_U,
      TYPE_DROIT.PEI_ADRESSE_C,
    ]);

    if (
      displayFicheResumeStandalone === true &&
      hasDroit(user, TYPE_DROIT.PEI_R)
    ) {
      listeButton.push({
        row: (row: PeiRowType) => row,
        type: TYPE_BUTTON.SIMPLE_MODAL,
        icon: <IconSee />,
        textEnable: "Voir le résumé",
        simpleModal: {
          content: (peiId: string, row: PeiRowType) => (
            <FicheResume
              peiId={peiId}
              peiNumeroComplet={row.original.peiNumeroComplet}
            />
          ),
          header: (row: PeiRowType) =>
            "Fiche Résumé du PEI " + row.original.peiNumeroComplet,
        },
      });
    }
    // Si l'utilisateur ne peut pas update un PEI, on lui propose la fiche complète en lecture seule
    if (!canEditPei) {
      listeButton.push({
        row: (row: PeiRowType) => row,
        type: TYPE_BUTTON.SEE,
        route: (idPei) => URLS.UPDATE_PEI(idPei),
        icon: <IconOeil />,
        textEnable: "Voir la fiche complète du PEI",
      });
    }
    if (canEditPei) {
      listeButton.push({
        row: (row: PeiRowType) => row,
        type: TYPE_BUTTON.UPDATE,
        route: (idPei) => URLS.UPDATE_PEI(idPei),
      });
    }
    if (hasDroit(user, TYPE_DROIT.PEI_R)) {
      listeButton.push({
        row: (row) => {
          return row;
        },
        type: TYPE_BUTTON.SIMPLE_MODAL,
        icon: <IconDocument />,
        textEnable: "Voir les documents",
        disable: (row) => !row.original.hasDocuments,
        textDisable: "Aucun document pour ce pei",
        simpleModal: {
          content: (peiId: string) => <DocumentPei peiId={peiId} />,
          header: (row) => "Documents du PEI " + row.original.peiNumeroComplet,
        },
      });
    }
    if (hasDroit(user, TYPE_DROIT.PEI_D)) {
      listeButton.push({
        disable: (v: PeiRowType) => v.original.hasTourneeReservee,
        textDisable:
          "Impossible de supprimer un PEI affecté à une tournée réservée",
        row: (row: PeiRowType) => row,
        type: TYPE_BUTTON.DELETE,
        pathname: url`/api/pei/delete/`,
        header: (row: PeiRowType) =>
          "Suppression du PEI " + row.original.peiNumeroComplet,
        content: (row: PeiRowType) => (
          <div>
            Êtes-vous sûr de vouloir supprimer le PEI{" "}
            <b>{row.original.peiNumeroComplet}</b> ? Cette action est
            irréversible.
          </div>
        ),
      });
    }
    if (hasDroit(user, TYPE_DROIT.VISITE_R)) {
      listeButton.push({
        row: (row: PeiRowType) => row,
        route: (idPei) => URLS.VISITE(idPei),
        type: TYPE_BUTTON.LINK,
        icon: <IconVisite />,
        textEnable: "Gérer les visites",
        classEnable: "warning",
      });
    }
    if (hasDroit(user, TYPE_DROIT.TOURNEE_R)) {
      listeButton.push({
        row: (row: PeiRowType) => row,
        type: TYPE_BUTTON.LINK,
        route: () => URLS.PEI_TOURNEE,
        search: (row: PeiRowType) => {
          const filter = JSON.stringify({
            peiId: row.original.peiId,
          });
          const searchParams = new URLSearchParams();
          searchParams.set("filterBy", filter);
          if (
            typeof pagination === "object" &&
            pagination !== null &&
            "limit" in pagination &&
            "offset" in pagination
          ) {
            searchParams.set("limit", String(pagination.limit ?? ""));
            searchParams.set("offset", String(pagination.offset ?? ""));
          }
          return searchParams.toString();
        },
        state: (row: PeiRowType) => ({
          peiNumeroComplet: row.original.peiNumeroComplet,
        }),
        icon: <IconList />,
        textEnable: "Voir les tournées associées",
      });
    }
    if (hasDroit(user, TYPE_DROIT.PEI_U)) {
      listeButton.push({
        row: (row: PeiRowType) => row,
        route: (idPei) => URLS.UPDATE_PENA_ASPIRATION(idPei),
        type: TYPE_BUTTON.LINK,
        icon: <IconAireAspiration />,
        textEnable: "Gérer les aires d'aspirations",
        classEnable: "warning",
        hide: (original: PeiRowType["original"]) => {
          return original.peiTypePei !== TYPE_PEI.PENA;
        },
      });
    }
    listeButton.push({
      row: (row: PeiRowType) => row,
      onClick: (idPei: string) => fetchGeometry(GET_TYPE_GEOMETRY.PEI, idPei),
      type: TYPE_BUTTON.LINK,
      icon: <IconLocation />,
      textEnable: "Localiser",
      classEnable: "primary",
    });
    column.push(
      ActionColumn({
        Header: "Actions",
        accessor: "peiId",
        buttons: listeButton,
      }),
    );
  }

  return column;
}

function getStatutIndispo(
  indisponibiliteTemporaireDateDebut: string,
  indisponibiliteTemporaireDateFin: string,
) {
  const dateDebut = new Date(indisponibiliteTemporaireDateDebut);
  const dateFin = new Date(indisponibiliteTemporaireDateFin);
  const date = new Date();
  let statut = "";
  if (
    dateDebut < date &&
    (!indisponibiliteTemporaireDateFin || dateFin > date)
  ) {
    statut = "En cours";
  } else if (dateDebut > date) {
    statut = "Planifiée";
  } else if (dateFin < date) {
    statut = "Terminée";
  }
  return statut;
}

/***********************INDISPO_TEMPORARIE******************/
export function GetColumnIndisponibiliteTemporaireByStringArray({
  user,
  parametres,
  handleButtonClick,
  fetchGeometry,
}: {
  user: UtilisateurEntity;
  parametres: Array<COLUMN_INDISPONIBILITE_TEMPORAIRE>;
  handleButtonClick: (value: string) => any;
  fetchGeometry: (typeGeometrie: string, idInsiponibiliteTemp: string) => void;
}): Array<columnType> {
  const listePeiState = useGet(url`/api/pei/get-id-numero`);
  const communeListState = useGet(url`/api/commune/get-libelle-commune`);
  const column: Array<columnType> = [];
  parametres.forEach((_parametre: COLUMN_INDISPONIBILITE_TEMPORAIRE) => {
    switch (_parametre) {
      case COLUMN_INDISPONIBILITE_TEMPORAIRE.MOTIF:
        column.push({
          Header: "Motif",
          accessor: "indisponibiliteTemporaireMotif",
          sortField: "indisponibiliteTemporaireMotif",
          Filter: (
            <FilterInput type="text" name="indisponibiliteTemporaireMotif" />
          ),
        });
        break;
      case COLUMN_INDISPONIBILITE_TEMPORAIRE.DESCRIPTION:
        column.push({
          Header: "Observation",
          accessor: "indisponibiliteTemporaireObservation",
          sortField: "indisponibiliteTemporaireObservation",
          Filter: (
            <FilterInput
              type="text"
              name="indisponibiliteTemporaireObservation"
            />
          ),
        });
        break;
      case COLUMN_INDISPONIBILITE_TEMPORAIRE.STATUT:
        column.push({
          Header: "Statut",
          accessor: ({
            indisponibiliteTemporaireDateDebut,
            indisponibiliteTemporaireDateFin,
          }: {
            indisponibiliteTemporaireDateDebut: Date;
            indisponibiliteTemporaireDateFin: Date;
          }) => ({
            indisponibiliteTemporaireDateDebut,
            indisponibiliteTemporaireDateFin,
          }),
          Cell: (value) => {
            return (
              <div>
                {getStatutIndispo(
                  value.value.indisponibiliteTemporaireDateDebut,
                  value.value.indisponibiliteTemporaireDateFin,
                )}
              </div>
            );
          },
          Filter: (
            <SelectEnumOption
              options={STATUT_INDISPONIBILITE_TEMPORAIRE}
              name={"indisponibiliteTemporaireStatut"}
            />
          ),
        });
        break;
      case COLUMN_INDISPONIBILITE_TEMPORAIRE.MAIL_AVANT:
        column.push(
          BooleanColumn({
            Header: "Notification avant date début",
            accessor: "indisponibiliteTemporaireMailAvantIndisponibilite",
            sortField: "indisponibiliteTemporaireMailAvantIndisponibilite",
            Filter: (
              <SelectEnumOption
                options={VRAI_FAUX}
                name={"indisponibiliteTemporaireMailAvantIndisponibilite"}
              />
            ),
          }),
        );

        break;
      case COLUMN_INDISPONIBILITE_TEMPORAIRE.MAIL_APRES:
        column.push(
          BooleanColumn({
            Header: "Notification avant date fin",
            accessor: "indisponibiliteTemporaireMailApresIndisponibilite",
            sortField: "indisponibiliteTemporaireMailApresIndisponibilite",
            Filter: (
              <SelectEnumOption
                options={VRAI_FAUX}
                name={"indisponibiliteTemporaireMailApresIndisponibilite"}
              />
            ),
          }),
        );

        break;

      case COLUMN_INDISPONIBILITE_TEMPORAIRE.LIST_PEI:
        column.push({
          Header: "PEI concernés",
          accessor: "listeNumeroPei",
          sortField: "listeNumeroPei",
          Filter: (
            <MultiSelectFilterFromList
              name={"listePeiId"}
              listIdCodeLibelle={listePeiState?.data?.map(
                (e: { peiId: any; peiNumeroComplet: any }) => ({
                  id: e.peiId,
                  code: e.peiId,
                  libelle: e.peiNumeroComplet,
                }),
              )}
            />
          ),
          width: 200,
        });
        break;

      case COLUMN_INDISPONIBILITE_TEMPORAIRE.DATE_DEBUT:
        column.push({
          Header: "Date de début",
          accessor: "indisponibiliteTemporaireDateDebut",
          sortField: "indisponibiliteTemporaireDateDebut",
          Cell: (value) => {
            return <div>{value.value ? formatDateTime(value.value) : ""}</div>;
          },
        });
        break;

      case COLUMN_INDISPONIBILITE_TEMPORAIRE.DATE_FIN:
        column.push({
          Header: "Date de fin",
          accessor: "indisponibiliteTemporaireDateFin",
          sortField: "indisponibiliteTemporaireDateFin",
          Cell: (value) => {
            return <div>{value.value ? formatDateTime(value.value) : ""}</div>;
          },
        });
        break;

      case COLUMN_INDISPONIBILITE_TEMPORAIRE.COMMUNE:
        column.push({
          Header: "Communes",
          accessor: "listeCommunes",
          Filter: (
            <MultiSelectFilterFromList
              name={"listeCommunes"}
              listIdCodeLibelle={communeListState?.data ?? []}
            />
          ),
          Cell: (value) => {
            let display = value.value;
            if (Array.isArray(display)) {
              display = display.join(", ");
            }
            return (
              <TooltipCustom
                tooltipText={display}
                tooltipId={display}
                maxWidth={167}
              >
                {display}
              </TooltipCustom>
            );
          },
          width: 170,
        });
        break;

      default:
    }
  });
  {
    const listeButton: ButtonType[] = [];
    listeButton.push({
      row: (row) => {
        return row;
      },
      type: TYPE_BUTTON.LINK,
      route: (indisponibiliteTemporaireId) =>
        URLS.INDISPONIBILITE_TEMPORAIRE_LISTE_PEI(indisponibiliteTemporaireId),
      state: (row: PeiRowType) => ({
        peiNumeroComplet: row.original.peiNumeroComplet,
      }),
      textEnable: "Lister les points d'eau",
      icon: <IconList />,
    });
    if (hasDroit(user, TYPE_DROIT.INDISPO_TEMP_U)) {
      listeButton.push({
        row: (row) => {
          return row;
        },
        type: TYPE_BUTTON.UPDATE,
        disable: (v) => {
          return (
            getStatutIndispo(
              v.original.indisponibiliteTemporaireDateDebut,
              v.original.indisponibiliteTemporaireDateFin,
            ) === STATUT_INDISPONIBILITE_TEMPORAIRE.TERMINEE ||
            !v.original.isModifiable
          );
        },
        textDisable:
          "L'indisponibilité temporaire est terminée ou contient des PEI en dehors de votre zone de compétence",
        route: (indisponibiliteTemporaireId) =>
          URLS.UPDATE_INDISPONIBILITE_TEMPORAIRE(indisponibiliteTemporaireId),
      });
    }
    if (hasDroit(user, TYPE_DROIT.INDISPO_TEMP_D)) {
      listeButton.push({
        row: (row) => {
          return row;
        },
        type: TYPE_BUTTON.DELETE,
        disable: (v) => {
          return (
            getStatutIndispo(
              v.original.indisponibiliteTemporaireDateDebut,
              v.original.indisponibiliteTemporaireDateFin,
            ) === STATUT_INDISPONIBILITE_TEMPORAIRE.EN_COURS ||
            !v.original.isModifiable
          );
        },
        textDisable:
          "L'indisponibilité temporaire est en cours ou contient des PEI en dehors de votre zone de compétence",
        pathname: url`/api/indisponibilite-temporaire/delete/`,
      });

      if (hasDroit(user, TYPE_DROIT.INDISPO_TEMP_U)) {
        listeButton.push({
          isPost: false,
          row: (row) => {
            return row;
          },
          type: TYPE_BUTTON.CONFIRM,
          disable: (v) => {
            return (
              getStatutIndispo(
                v.original.indisponibiliteTemporaireDateDebut,
                v.original.indisponibiliteTemporaireDateFin,
              ) !== STATUT_INDISPONIBILITE_TEMPORAIRE.EN_COURS
            );
          },
          confirmModal: {
            header: "Clore l'indisponibilité temporaire ?",
            content: "Voulez-vous continuer ? ",
          },
          textDisable:
            "Impossible de clore une indisponibilité temporaire qui n'est pas en cours",
          textEnable: "Clore l'indisponibilité temporaire",
          pathname: url`/api/indisponibilite-temporaire/clore/`,
          icon: <IconCloseIndisponibiliteTemporaire />,
          classEnable: "warning",
        });
      }
    }
    listeButton.push({
      row: (row) => {
        return row;
      },
      onClick: (idIndisponibiliteTemporaire) =>
        fetchGeometry(
          GET_TYPE_GEOMETRY.INDISPONIBILITE_TEMP,
          idIndisponibiliteTemporaire,
        ),
      type: TYPE_BUTTON.LINK,
      icon: <IconLocation />,
      textEnable: "Localiser",
      classEnable: "primary",
    });
    column.push(
      ActionColumn({
        Header: "Actions",
        accessor: "indisponibiliteTemporaireId",
        buttons: listeButton,
      }),
    );
  }

  return column;
}
/**
 * Composant React qui affiche une date stylisée selon son niveau d'urgence.
 *
 * La date est comparée à `now + paramUrgent` et `now + paramWarning` :
 * - Si elle est avant `now + paramUrgent`, elle est considérée comme urgente (texte en rouge)
 * - Si elle est avant `now + paramWarning`, elle est considérée comme un avertissement (texte en orange/jaune)
 * - Sinon, elle est affichée normalement
 */
const DateHighlightCell = ({
  date,
  paramUrgent,
  paramWarning,
}: {
  date: Date;
  paramUrgent: number;
  paramWarning: number;
}) => {
  const now = new Date();

  const dateUrgent = new Date(now);
  dateUrgent.setDate(now.getDate() + Number(paramUrgent));

  const dateWarning = new Date(now);
  dateWarning.setDate(now.getDate() + Number(paramWarning));

  const dateToCompare = new Date(date); // pour la mettre au même format que les autres peu importe l'entrée

  let isUrgent = false,
    isWarning = false;

  if (dateToCompare < dateUrgent) {
    isUrgent = true;
  } else if (dateToCompare < dateWarning) {
    isWarning = true;
  }

  return (
    <div
      className={classnames(
        "text-center",
        isUrgent ? "text-danger" : isWarning ? "text-warning" : "",
      )}
    >
      {date ? (
        isUrgent || isWarning ? (
          <b>{formatDateWithFallback(date)}</b>
        ) : (
          formatDateWithFallback(date)
        )
      ) : (
        ""
      )}
    </div>
  );
};
export default getColumnPeiByStringArray;
